/**
 * useEnvironment Hook
 * Fetches environment information from the /health endpoint
 * Constitution v1.8.0 - Multi-Subscription SQL MI Edition
 */

import { useState, useEffect, useCallback } from 'react';

export interface EnvironmentInfo {
  environment: 'development' | 'uat' | 'production';
  keyVault: string;
  sqlMi: string;
  database: string;
  subscription?: string;
}

export interface HealthResponse {
  status: 'healthy' | 'unhealthy';
  timestamp: string;
  version: string;
  deployStamp: string | null;
  constitution: string;
  environment: string;
  uptime: number;
  keyVault: string;
  sqlMi: string;
  database: string;
  subscription?: string;
}

export interface UseEnvironmentResult {
  environmentInfo: EnvironmentInfo | null;
  version: string | null;
  deployStamp: string | null;
  constitution: string | null;
  isLoading: boolean;
  error: Error | null;
  refresh: () => Promise<void>;
}

const API_BASE_URL = import.meta.env.VITE_API_URL || '';

export function useEnvironment(): UseEnvironmentResult {
  const [environmentInfo, setEnvironmentInfo] = useState<EnvironmentInfo | null>(null);
  const [version, setVersion] = useState<string | null>(null);
  const [deployStamp, setDeployStamp] = useState<string | null>(null);
  const [constitution, setConstitution] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchEnvironment = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await fetch(`${API_BASE_URL}/api/health`);

      if (!response.ok) {
        throw new Error(`Health check failed: ${response.status}`);
      }

      const data: HealthResponse = await response.json();

      setEnvironmentInfo({
        environment: data.environment as EnvironmentInfo['environment'],
        keyVault: data.keyVault,
        sqlMi: data.sqlMi,
        database: data.database,
        subscription: data.subscription,
      });
      setVersion(data.version);
      setDeployStamp(data.deployStamp ?? null);
      setConstitution(data.constitution);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch environment'));
      console.error('Failed to fetch environment info:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchEnvironment();
  }, [fetchEnvironment]);

  return {
    environmentInfo,
    version,
    deployStamp,
    constitution,
    isLoading,
    error,
    refresh: fetchEnvironment,
  };
}

export default useEnvironment;
